/**
 * InstallPWA Component
 * ========================================
 * Popup ເຊີນລູກຄ້າ ຕິດຕັ້ງ PWA ລົງໜ້າຈໍ
 * ✅ Android (Chrome) — ໃຊ້ beforeinstallprompt event
 * ✅ iOS (Safari) — ສະແດງ ວິທີກົດ Share → Add to Home Screen
 * ========================================
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./InstallPWA.css";

// ============================================================
// Helper: ກວດ Platform
// ============================================================
const isIOS = () => {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

const isInStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

// ============================================================
// Constants
// ============================================================
const DISMISSED_KEY = "pwa_install_dismissed";
const INSTALLED_KEY = "pwa_installed";
const DISMISS_DAYS = 7; // ຖ້າກົດ "ບໍ່ແມ່ນຕອນນີ້" ຈະບໍ່ສະແດງ 7 ມື້

// ============================================================
// Component
// ============================================================
export default function InstallPWA() {
  const { t } = useTranslation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState(null); // "android" | "ios" | null
  const [installing, setInstalling] = useState(false);

  // ============================================================
  // ກວດວ່າ ຄວນສະແດງ popup ຫຼືບໍ່
  // ============================================================
  const shouldShowPrompt = useCallback(() => {
    // ຖ້າ ເປີດໃນ standalone mode ແລ້ວ (ຕິດຕັ້ງແລ້ວ) → ບໍ່ສະແດງ
    if (isInStandaloneMode()) return false;
    
    // ຖ້າ ເຄີຍຕິດຕັ້ງແລ້ວ → ບໍ່ສະແດງ
    if (localStorage.getItem(INSTALLED_KEY)) return false;

    // ຖ້າ ເຄີຍກົດ "ບໍ່ແມ່ນຕອນນີ້" → ກວດເວລາ
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const daysPassed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysPassed < DISMISS_DAYS) return false;
    }

    return true;
  }, []);

  // ============================================================
  // Event: beforeinstallprompt (Android Chrome)
  // ============================================================
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform("android");
      
      if (shouldShowPrompt()) {
        // ລໍ 2 ວິ ກ່ອນສະແດງ (ໃຫ້ໜ້າ load ກ່ອນ)
        setTimeout(() => setShowPrompt(true), 2000);
      }
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Detect install success
    window.addEventListener("appinstalled", () => {
      localStorage.setItem(INSTALLED_KEY, "true");
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [shouldShowPrompt]);

  // ============================================================
  // iOS: ສະແດງ popup ສອນກົດ Share
  // ============================================================
  useEffect(() => {
    if (isIOS() && !isInStandaloneMode() && shouldShowPrompt()) {
      setPlatform("ios");
      // ລໍ 3 ວິ ສຳລັບ iOS
      setTimeout(() => setShowPrompt(true), 3000);
    }
  }, [shouldShowPrompt]);

  // ============================================================
  // ກົດ "ຕິດຕັ້ງ" (Android)
  // ============================================================
  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        localStorage.setItem(INSTALLED_KEY, "true");
      }
    } catch (err) {
      console.error("Install error:", err);
    } finally {
      setDeferredPrompt(null);
      setInstalling(false);
      setShowPrompt(false);
    }
  };

  // ============================================================
  // ກົດ "ບໍ່ແມ່ນຕອນນີ້"
  // ============================================================
  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    setShowPrompt(false);
  };

  // ============================================================
  // ບໍ່ສະແດງ
  // ============================================================
  if (!showPrompt) return null;

  // ============================================================
  // Render
  // ============================================================
  return (
    <>
      {/* ===== Backdrop ===== */}
      <div style={styles.backdrop} onClick={handleDismiss} />

      {/* ===== Popup ===== */}
      <div style={styles.popup}>
        {/* Close button */}
        <button style={styles.closeBtn} onClick={handleDismiss} aria-label="Close">
          ✕
        </button>

        {/* App Icon */}
        <div style={styles.iconWrapper}>
          <img
            src="/PPShopFinal.jpg"
            alt="PPShope"
            style={styles.appIcon}
          />
        </div>

        {/* Title */}
        <h3 style={styles.title}>{t("InstallTitle")}</h3>
        <p style={styles.subtitle}>
          {t("InstallSubtitle")}
        </p>

        {/* Benefits */}
        <div style={styles.benefits}>
          <div style={styles.benefitItem}>
            <span style={styles.benefitIcon}>⚡</span>
            <span style={styles.benefitText}>{t("InstallBenefit1")}</span>
          </div>
          <div style={styles.benefitItem}>
            <span style={styles.benefitIcon}>📱</span>
            <span style={styles.benefitText}>{t("InstallBenefit2")}</span>
          </div>
          <div style={styles.benefitItem}>
            <span style={styles.benefitIcon}>🔔</span>
            <span style={styles.benefitText}>{t("InstallBenefit3")}</span>
          </div>
        </div>

        {/* ===== Android: ປຸ່ມ Install ===== */}
        {platform === "android" && (
          <div style={styles.actions}>
            <button
              style={styles.installBtn}
              onClick={handleInstall}
              disabled={installing}
            >
              {installing ? (
                <span>{t("InstallInstalling")}</span>
              ) : (
                <>
                  <span style={{ marginRight: 8 }}>📥</span>
                  {t("InstallBtn")}
                </>
              )}
            </button>
            <button style={styles.dismissBtn} onClick={handleDismiss}>
              {t("InstallNotNow")}
            </button>
          </div>
        )}

        {/* ===== iOS: ສອນກົດ Share ===== */}
        {platform === "ios" && (
          <div style={styles.iosGuide}>
            <p style={styles.iosTitle}>{t("InstallIOSGuide")}</p>
            <div style={styles.iosSteps}>
              <div style={styles.iosStep}>
                <span style={styles.stepNumber}>1</span>
                <span style={styles.stepText}>
                  {t("InstallIOSStep1")}{" "}
                  <span style={styles.shareIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: "middle" }}>
                      <path d="M12 2L12 15M12 2L8 6M12 2L16 6" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M4 12V20C4 20.5523 4.44772 21 5 21H19C19.5523 21 20 20.5523 20 20V12" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </span>
              </div>
              <div style={styles.iosStep}>
                <span style={styles.stepNumber}>2</span>
                <span style={styles.stepText}>
                  {t("InstallIOSStep2")}
                </span>
              </div>
              <div style={styles.iosStep}>
                <span style={styles.stepNumber}>3</span>
                <span style={styles.stepText}>
                  {t("InstallIOSStep3")}
                </span>
              </div>
            </div>
            <button style={styles.dismissBtn} onClick={handleDismiss}>
              {t("InstallIOSOk")}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================
// Styles (inline — ບໍ່ຕ້ອງ import CSS ເພີ່ມ)
// ============================================================
const styles = {
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: 99998,
    animation: "fadeIn 0.3s ease",
  },
  popup: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#1a1a2e",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: "28px 24px 32px",
    zIndex: 99999,
    animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
    boxShadow: "0 -8px 32px rgba(0,0,0,0.5)",
    maxWidth: 480,
    margin: "0 auto",
  },
  closeBtn: {
    position: "absolute",
    top: 12,
    right: 16,
    background: "none",
    border: "none",
    color: "#888",
    fontSize: 20,
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
  },
  iconWrapper: {
    textAlign: "center",
    marginBottom: 12,
  },
  appIcon: {
    width: 72,
    height: 72,
    borderRadius: 16,
    boxShadow: "0 4px 16px rgba(255, 215, 0, 0.3)",
    border: "2px solid rgba(255, 215, 0, 0.4)",
  },
  title: {
    textAlign: "center",
    color: "#ffffff",
    fontSize: 20,
    fontWeight: 700,
    margin: "0 0 4px 0",
    fontFamily: "'Noto Sans Lao', sans-serif",
  },
  subtitle: {
    textAlign: "center",
    color: "#aaa",
    fontSize: 14,
    margin: "0 0 16px 0",
    fontFamily: "'Noto Sans Lao', sans-serif",
  },
  benefits: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 20,
    padding: "12px 16px",
    backgroundColor: "rgba(255, 215, 0, 0.06)",
    borderRadius: 12,
    border: "1px solid rgba(255, 215, 0, 0.12)",
  },
  benefitItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  benefitIcon: {
    fontSize: 16,
  },
  benefitText: {
    color: "#ddd",
    fontSize: 13,
    fontFamily: "'Noto Sans Lao', sans-serif",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  installBtn: {
    width: "100%",
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 700,
    fontFamily: "'Noto Sans Lao', sans-serif",
    color: "#0a0a0f",
    background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
    border: "none",
    borderRadius: 14,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 16px rgba(255, 215, 0, 0.35)",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  dismissBtn: {
    width: "100%",
    padding: "12px 24px",
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "'Noto Sans Lao', sans-serif",
    color: "#888",
    backgroundColor: "transparent",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 14,
    cursor: "pointer",
    transition: "background 0.2s",
  },
  // iOS Guide
  iosGuide: {
    marginTop: 4,
  },
  iosTitle: {
    color: "#FFD700",
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 12,
    fontFamily: "'Noto Sans Lao', sans-serif",
  },
  iosSteps: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 20,
  },
  iosStep: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 215, 0, 0.15)",
    color: "#FFD700",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepText: {
    color: "#ccc",
    fontSize: 13,
    fontFamily: "'Noto Sans Lao', sans-serif",
  },
  shareIcon: {
    display: "inline-flex",
    verticalAlign: "middle",
  },
};
